/**
 * pricing-editor.ts — Kiểu dữ liệu + hàm thuần cho trang Platform → Bảng giá gói.
 *
 * Kiểu khớp với backend `ecdp_backend_source/src/modules/billing/pricing.ts`.
 * `validatePricing` chỉ là kiểm tra sơ bộ phía client (giống backend) để báo sớm;
 * thông báo lỗi của server khi lưu / xem thử mới là nguồn chuẩn.
 */

export type QuotaKey = 'members' | 'officialChannels' | 'personalChannels'
export const QUOTA_KEYS: readonly QuotaKey[] = ['members', 'officialChannels', 'personalChannels']
export const QUOTA_LABELS: Record<QuotaKey, string> = {
  members: 'Nhân viên',
  officialChannels: 'Kênh chính thức',
  personalChannels: 'Tài khoản cá nhân',
}

export interface PriceTier {
  upTo: number | null
  unitPrice: number
}

export type Quotas = Record<QuotaKey, number>

export interface PricingPlan {
  id: string
  name: string
  description: string
  monthlyPrice: number | null
  included: Quotas
  max: Record<QuotaKey, number | null>
  activeContactsPerMonth: number | null
  aiRepliesPerDay: number | null
  /** AI hai chế độ: credit AI tặng mỗi tháng (null = không tặng). */
  aiCreditsPerMonth?: number | null
  /** Trần nhúng tài liệu miễn phí / ngày. */
  aiEmbedFreePerDay?: number | null
  /** Trần credit AI của eCDP / ngày (null = không trần). */
  aiPlatformDailyCap?: number | null
  /** Dòng mở đầu danh sách tính năng (trang Gói dịch vụ + popup chào mừng). Server cũ: không có. */
  featuresIntro?: string
  /** Tính năng của gói, mỗi mục một dòng. Server cũ: không có. */
  features?: string[]
  purchasable: boolean
  highlight: boolean
  hidden: boolean
}

export interface DurationOption {
  days: number
  discountPercent: number
  badge: string
}

export interface PricingConfig {
  version: 1
  currency: 'VND'
  plans: PricingPlan[]
  addons: Record<QuotaKey, PriceTier[]>
  durations: DurationOption[]
  roundTo: number
  defaultDays: number
  note: string
  /** Thuế suất VAT (%) cộng thêm khi thanh toán — giá niêm yết chưa gồm VAT (Q2). 0 = không chịu thuế. */
  vatPercent?: number
  /** Tỷ giá VND / 1 USD thu PayPal; bỏ trống = theo biến môi trường server (không có → ẩn PayPal). */
  paypalVndPerUsd?: number | null
}

/** Thuế suất mặc định khi cấu hình chưa có (khớp DEFAULT_VAT_PERCENT phía server). */
export const DEFAULT_VAT_PERCENT = 10

/** Thuế suất hoá đơn điện tử SePay hỗ trợ — ngoài các mức này hoá đơn không xuất được. */
export const EINVOICE_VAT_RATES = [0, 5, 8, 10] as const

export interface QuoteLine {
  key: QuotaKey
  label: string
  quantity: number
  included: number
  extra: number
  monthly: number
}

export interface Quote {
  planId: string
  planName: string
  days: number
  quantities: Quotas
  basePerMonth: number
  lines: QuoteLine[]
  perMonth: number
  subtotal: number
  discountPercent: number
  discount: number
  total: number
  vat?: { percent: number; preTax: number; amount: number }
}

export interface PricingResponse {
  pricing: PricingConfig
  defaults?: PricingConfig
  updatedAt: string | null
  isDefault: boolean
}

/** Mã gói có sẵn từ đầu — không cho đổi mã (tổ chức đang dùng các mã này). */
export const BUILTIN_PLAN_IDS = ['free', 'starter', 'pro', 'enterprise'] as const
export const PLAN_ID_RE = /^[a-z][a-z0-9_-]{1,31}$/

// ── Ô nhập số ─────────────────────────────────────────────────────

/** "1.490.000" → 1490000; "" → null. Bỏ mọi ký tự không phải chữ số. */
export function parseIntInput(s: string): number | null {
  const digits = s.replace(/\D/g, '')
  if (!digits) return null
  return Number(digits.slice(0, 15))
}

/** 1490000 → "1.490.000"; null → "". */
export function formatIntInput(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return ''
  return new Intl.NumberFormat('vi-VN').format(n)
}

// ── Bậc giá luỹ tiến ──────────────────────────────────────────────

/** Khoảng đơn vị mỗi bậc áp dụng: bậc i từ (upTo bậc trước + 1) đến upTo (null = trở lên). */
export function tierRanges(tiers: PriceTier[]): { from: number; to: number | null }[] {
  let prev = 0
  return tiers.map((t) => {
    const r = { from: prev + 1, to: t.upTo }
    prev = t.upTo ?? prev
    return r
  })
}

/** Thêm một bậc: bậc cuối (trở lên) được chốt mốc "đến" = từ + 4, bậc mới thành bậc cuối. */
export function addTier(tiers: PriceTier[]): PriceTier[] {
  if (tiers.length === 0) return [{ upTo: null, unitPrice: 0 }]
  const ranges = tierRanges(tiers)
  const lastIdx = tiers.length - 1
  const last = tiers[lastIdx]
  const closed = { ...last, upTo: ranges[lastIdx].from + 4 }
  return [...tiers.slice(0, lastIdx), closed, { upTo: null, unitPrice: last.unitPrice }]
}

/** Xoá bậc i; bậc cuối luôn là "trở lên". Không xoá khi chỉ còn 1 bậc. */
export function removeTier(tiers: PriceTier[], i: number): PriceTier[] {
  if (tiers.length <= 1) return tiers
  const next = tiers.filter((_, j) => j !== i)
  next[next.length - 1] = { ...next[next.length - 1], upTo: null }
  return next
}

/** Phụ phí / tháng cho các đơn vị thứ (from+1 .. to) — giống backend `tieredCost`. */
export function tieredCost(tiers: PriceTier[], from: number, to: number): number {
  let total = 0
  let k = from
  for (const t of tiers) {
    if (k >= to) break
    const cap = t.upTo == null ? to : Math.min(t.upTo, to)
    if (cap > k) {
      total += (cap - k) * t.unitPrice
      k = cap
    }
  }
  return total
}

// ── Gói ───────────────────────────────────────────────────────────

export const FEATURES_MAX = 12
export const FEATURE_MAX_LEN = 120

/** Ô nhập "mỗi dòng một tính năng" ⇄ mảng (bỏ dòng trống, cắt khoảng trắng). */
export const featuresToText = (f: string[] | undefined) => (f ?? []).join('\n')
export const textToFeatures = (t: string) => t.split('\n').map((l) => l.trim()).filter(Boolean)

export function newPlan(existingIds: string[]): PricingPlan {
  let id = 'goi-moi'
  for (let n = 2; existingIds.includes(id); n++) id = `goi-moi-${n}`
  return {
    id,
    name: 'Gói mới',
    description: '',
    monthlyPrice: null,
    included: { members: 1, officialChannels: 1, personalChannels: 0 },
    max: { members: null, officialChannels: null, personalChannels: null },
    activeContactsPerMonth: null,
    aiRepliesPerDay: null,
    featuresIntro: '',
    features: [],
    purchasable: false,
    highlight: false,
    hidden: true,
  }
}

// ── Kiểm tra sơ bộ (giống parsePricingConfig ở backend) ───────────

function isInt(n: unknown, min: number, max: number): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max
}

/** Danh sách lỗi (tiếng Việt). Rỗng = hợp lệ theo kiểm tra phía client. */
export function validatePricing(cfg: PricingConfig): string[] {
  const errs: string[] = []
  if (cfg.plans.length === 0 || cfg.plans.length > 8) errs.push('Cần 1–8 gói')
  const seen = new Set<string>()
  cfg.plans.forEach((p, i) => {
    const label = p.name.trim() || p.id || `Gói ${i + 1}`
    if (!PLAN_ID_RE.test(p.id)) errs.push(`Gói ${label}: mã chỉ gồm chữ thường, số, "-", "_" (2–32 ký tự, bắt đầu bằng chữ)`)
    if (seen.has(p.id)) errs.push(`Mã gói "${p.id}" bị trùng`)
    seen.add(p.id)
    if (!p.name.trim()) errs.push(`Gói ${i + 1}: tên không được để trống`)
    if (p.name.trim().length > 40) errs.push(`Gói ${label}: tên tối đa 40 ký tự`)
    if (p.description.trim().length > 200) errs.push(`Gói ${label}: mô tả tối đa 200 ký tự`)
    if ((p.featuresIntro ?? '').trim().length > 80) errs.push(`Gói ${label}: dòng mở đầu tính năng tối đa 80 ký tự`)
    const feats = (p.features ?? []).map((f) => f.trim()).filter(Boolean)
    if (feats.length > FEATURES_MAX) errs.push(`Gói ${label}: tối đa ${FEATURES_MAX} tính năng`)
    if (feats.some((f) => f.length > FEATURE_MAX_LEN)) errs.push(`Gói ${label}: mỗi tính năng tối đa ${FEATURE_MAX_LEN} ký tự`)
    for (const k of QUOTA_KEYS) {
      const inc = p.included[k]
      const mx = p.max[k]
      if (!isInt(inc, 0, 100_000)) errs.push(`Gói ${label} — ${QUOTA_LABELS[k]} gồm sẵn: 0–100.000`)
      if (mx != null && mx < inc) errs.push(`Gói ${label} — ${QUOTA_LABELS[k]}: tối đa phải ≥ số gồm sẵn`)
    }
    if (p.purchasable && (p.monthlyPrice == null || p.monthlyPrice <= 0)) {
      errs.push(`Gói ${label}: bán trực tuyến thì giá/tháng phải > 0`)
    }
  })
  if (!cfg.plans.some((p) => p.id === 'free')) errs.push('Phải có gói "free" (gói khi hết hạn sẽ hạ về)')

  for (const k of QUOTA_KEYS) {
    const tiers = cfg.addons[k] ?? []
    const f = `Phụ phí ${QUOTA_LABELS[k]}`
    if (tiers.length === 0 || tiers.length > 10) errs.push(`${f}: cần 1–10 bậc giá`)
    tiers.forEach((t, i) => {
      const last = i === tiers.length - 1
      if (!last && t.upTo == null) errs.push(`${f}: bậc ${i + 1} cần mốc "đến" (chỉ bậc cuối được để trống)`)
      if (last && t.upTo != null) errs.push(`${f}: bậc cuối phải để trống "đến" (trở lên)`)
      const prev = i > 0 ? tiers[i - 1].upTo : null
      if (t.upTo != null && prev != null && t.upTo <= prev) errs.push(`${f}: mốc "đến" phải tăng dần (bậc ${i + 1})`)
    })
  }

  if (cfg.durations.length === 0 || cfg.durations.length > 8) errs.push('Cần 1–8 mốc thời hạn')
  cfg.durations.forEach((d, i) => {
    if (!isInt(d.days, 1, 1_100)) errs.push(`Thời hạn ${i + 1}: số ngày từ 1 đến 1.100`)
    if (!isInt(d.discountPercent, 0, 90)) errs.push(`Thời hạn ${i + 1}: % giảm từ 0 đến 90`)
    if (d.badge.trim().length > 16) errs.push(`Thời hạn ${i + 1}: nhãn tối đa 16 ký tự`)
  })
  const days = cfg.durations.map((d) => d.days)
  if (new Set(days).size !== days.length) errs.push('Các mốc thời hạn bị trùng số ngày')
  if (!days.includes(cfg.defaultDays)) errs.push('Chọn một mốc thời hạn làm mặc định')
  if (!isInt(cfg.roundTo, 1, 1_000_000)) errs.push('Bước làm tròn: từ 1 đến 1.000.000')
  if (cfg.note.trim().length > 300) errs.push('Ghi chú: tối đa 300 ký tự')
  if (cfg.vatPercent != null && !isInt(cfg.vatPercent, 0, 100)) errs.push('Thuế suất VAT: số nguyên từ 0 đến 100 (0 = không chịu thuế)')
  if (cfg.paypalVndPerUsd != null && !isInt(cfg.paypalVndPerUsd, 1_000, 1_000_000)) errs.push('Tỷ giá PayPal: số nguyên từ 1.000 đến 1.000.000 (VND / 1 USD) hoặc để trống')
  return errs
}

/** So sánh hai cấu hình (để biết có thay đổi chưa lưu). */
export function samePricing(a: PricingConfig | null | undefined, b: PricingConfig | null | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}
